import { connectRouter, routerMiddleware } from "connected-react-router";
import { createBrowserHistory } from "history";
import debounce from "lodash.debounce";
import { applyMiddleware, combineReducers, compose, createStore } from "redux";
import { batchDispatchMiddleware } from "redux-batched-actions";
import { batchedSubscribe } from "redux-batched-subscribe";
import { persistReducer, persistStore } from "redux-persist";
import storage from "redux-persist/lib/storage";
import thunk from "redux-thunk";
import {
  addAdditionalUnauthorizedErrorHandler,
  addCaptchaHandler,
  addSessionTokenRefreshHandler,
} from "api/utils/enhancedFetch";
import { reducer as chat } from "chat/exports/state/reducer";
import {
  addChatGiftToPlayMiddleware,
  chatGiftAnimationReducer as chatGiftAnimation,
} from "chat/giftAnimation/export";
import { messageRequestReducer as messageRequest } from "chat/messageRequest/exports/state";
import { unlockListenerMiddleware } from "chat/premiumMessage/exports/middlewares";
import { premiumMessageReducer as premiumMessage } from "chat/premiumMessage/exports/state";
import { getEnvironmentName, getHooks } from "environment";
import { TRANSACTION_STATUS_KEY } from "src/core/analytics/constants";
import { makeIndexedDBPersistConfig } from "src/core/storage/indexedDB/makeIndexedDBPersistConfig";
import { appealSliceReducer as appeal } from "src/features/appeal/AppealPage/exports/state";
import { autoLoginReducer as autoLogin } from "src/features/autoLogin/exports/state";
import { clearBannerShownEventAfterRegistrationMiddleware } from "src/features/banners/state/clearBannerShownEventAfterRegistrationMiddleware";
import { invitationsReducer } from "src/features/broadcast/exports/state";
import { broadcastExternalReducer } from "src/features/broadcastExternal/exports/state";
import {
  broadcastMasksReducer as broadcastMasks,
  persistConfig as broadcastMasksPersistConfig,
} from "src/features/broadcastMasks/exports/state";
import { mosChatV2 } from "src/features/chat/exports/state/reducer";
import { liveBarReducer as liveBar } from "src/features/chat/liveBar/state/slice";
import { customGiftReducer as customGift } from "src/features/customGift/exports/state";
import { feedReducer as feed } from "src/features/feed/exports/state/reducer";
import { redeemConfig } from "src/features/finance/redeems/exports/reducer";
import { bannerGlobalConfigs } from "src/features/getTheApp/exports/state/reducers";
import { gifterUsersList } from "src/features/gifterStatistics/exports/state/reducer";
import {
  giftDrawerReducer as giftDrawer,
  persistConfig as giftDrawerPersistConfig,
} from "src/features/giftsDrawer/exports/state";
import { homePageStreams } from "src/features/homePage/shared/streamsState/streamsSlice";
import {
  homePageCreatorsReducer as homePageCreators,
  homePageGamesReducer as homePageGames,
  homePageProfilesReducer as homePageProfiles,
} from "src/features/homePage/v2/exports/state";
import { homePageV2Nearby } from "src/features/homePage/v2/state/nearbySlice";
import { loyaltyProgramReducer as loyaltyProgram } from "src/features/loyaltyProgram/exports/state";
import {
  marketingReducer as marketing,
  persistConfig as marketingPersistConfig,
} from "src/features/marketing/exports/state";
import { mosReducer as mos } from "src/features/mos/exports/state";
import { welcomePremiumOfferReducer as welcomePremiumOffer } from "src/features/offers/premiumEntrance/welcomePremiumOffer/exports/state";
import { buyEntranceOnBalanceUpdate } from "src/features/offers/premiumEntrance/welcomePremiumOffer/middlewares/buyEntranceOnBalanceUpdate";
import liveNotificationsSlice from "src/features/profile/settings/state/liveNotificationsSlice";
import {
  phoneNumberSettingsReducer as phoneNumberSettings,
  persistConfig as phoneNumberSettingsPersistConfig,
} from "src/features/profile/settings/state/phoneNumberSettingsSlice";
import profileSettingsSlice from "src/features/profile/settings/state/profileSettingsSlice";
import {
  clearVisitorProfileAfterLoginMiddleware,
  visitorProfilerReducer as visitorProfile,
} from "src/features/profile/visitorProfile/exports/state";
import bottomSheet from "src/features/promotions/promoBottomSheet/state/bottomSheetSlice";
import { recommendationsEventsMiddleware } from "src/features/recommendations/middlewares/recommendationsEventsMiddleware";
import { recommendationsSetupMiddleware } from "src/features/recommendations/middlewares/recommendationsSetupMiddleware";
import {
  referralProgramReducer as referralProgram,
  persistConfig as referralProgramPersistConfig,
} from "src/features/referralProgram/exports/state";
import {
  loginPersistConfig,
  loginReducer,
} from "src/features/signin/exports/state/reducer";
import { validationMiddleware } from "src/features/signin/state/login/validationMiddleware";
import {
  socialGamesFreespinsReducer as socialGamesFreeSpins,
  socialGamesGameReducer as socialGamesGame,
  socialGamesInStreamLobbyReducer as socialGamesInStreamLobby,
  socialGamesLobbyReducer as socialGamesLobby,
} from "src/features/socialGames/exports/state";
import { tangoAcademyReducer } from "src/features/tangoAcademy/exports/state";
import {
  tutorialsPersistConfig,
  tutorialsReducer,
} from "src/features/tutorials/shared/exports/state";
import { visitorCashierReducer as visitorCashier } from "src/features/visitorCashier/exports/state";
import { openWelcomeOnboardingOfferAfterRegistrationMiddleware } from "src/features/welcomeOnboardingOffer/exports";
import welcomeOnboardingOffer from "src/features/welcomeOnboardingOffer/state/welcomeOfferSlice";
import { sendGiftQueue } from "src/state/flows/sendGift";
import { bonusWheelReducer as bonusWheel } from "src/state/tree/bonusWheel";
import giftsCache, {
  persistConfig as giftsCachePersistConfig,
} from "src/state/tree/giftsCache";
import { pipsConfigReducer as pipsConfig } from "src/state/tree/pipsConfig";
import { pointsUserInfo } from "src/state/tree/pointsSlice";
import { clearPersistLandingPageKey } from "src/utils/clearPersistLandingPageKey";
import createCaptchaHandler from "src/utils/createCaptchaHandler";
import createSessionTokenRefreshHandler from "src/utils/createSessionTokenRefreshHandler";
import createUnauthorizedErrorHandler from "src/utils/createUnauthorizedErrorHandler";
import {
  getIsBatchingReduxStoreEnabled,
  getTimeForReduxBatchingStore,
} from "state/abTests";
import injectJoshGoBack from "state/injectJoshGoBack";
import accountIdChangeObserver from "state/middleware/accountIdChangeObserver";
import acmeObserver from "state/middleware/acmeObserver";
import analyticsListener from "state/middleware/analyticsListener";
import { BIAccountIdMiddleware } from "state/middleware/biUserIDMiddleware";
import { financeServiceAcmeMiddleware } from "state/middleware/financeServiceAcmeMiddleware";
import openCashierAfterAutoLoginFailure from "state/middleware/openCashierAfterAutoLoginFailure";
import pendingActionsListener from "state/middleware/pendingActionsListener";
import { pullEventListenerMiddleware } from "state/middleware/pullEventListener";
import { settingServiceAcmeMiddleware } from "state/middleware/settingServiceAcmeMiddleware";
import afterPurchaseTaskManager from "state/tree/afterPurchaseTaskManager";
import bannerConfigs from "state/tree/bannerConfigs";
import blockedUsers from "state/tree/blockedUsers";
import bottomScreen from "state/tree/bottomScreen";
import broadcast, { broadcastPersistConfig } from "state/tree/broadcast";
import captcha, {
  persistConfig as captchaPersistConfig,
} from "state/tree/captcha";
import configuration, {
  persistConfig as configurationPersistConfig,
} from "state/tree/configuration";
import connectionManager, {
  persistConfig as cmPersistConfig,
} from "state/tree/connectionManager";
import countries from "state/tree/countries";
import countryByIp from "state/tree/countryByIp";
import creditCards, {
  persistConfig as creditCardsPersistConfig,
} from "state/tree/creditCards";
import currencies from "state/tree/currencies";
import customDrawer, {
  persistConfig as customDrawerPersistConfig,
} from "state/tree/customDrawer";
import deviceInfo, {
  persistConfig as deviceInfoPersistConfig,
} from "state/tree/deviceInfo";
import dropdownMenu from "state/tree/dropdownMenu";
import followingList, {
  persistConfig as followingListPersistConfig,
} from "state/tree/followingList";
import giftingRequests from "state/tree/giftingRequests";
import guardian from "state/tree/guardian";
import homePageV1, {
  homePageV1PersistConfig,
} from "state/tree/homePageV1/homePageV1";
import intercom from "state/tree/intercom";
import landingPagePhoenix, {
  persistConfig as landingPagePhoenixConfigPersistConfig,
} from "state/tree/landingPagePhoenix";
import leaderBoardLanding from "state/tree/leaderBoardLanding";
import liveStreamsFeed, {
  persistConfig as liveStreamsFeedPersistConfig,
} from "state/tree/liveStreamsFeed";
import modal from "state/tree/modal";
import navigation from "state/tree/navigation";
import nearbyCountries, {
  persistConfig as nearbyCountriesPersistConfig,
} from "state/tree/nearbyCountries";
import networkAvailability from "state/tree/networkAvailability";
import nftAuction from "state/tree/nft";
import notifications from "state/tree/notifications";
import personalOffers, {
  persistConfig as personalOffersConfig,
} from "state/tree/personalOffers";
import postsFeed from "state/tree/postsFeed";
import profilesCache, {
  persistConfig as profilesCachePersistConfig,
} from "state/tree/profilesCache";
import receivedAcmeIds, {
  persistConfig as receivedAcmeIdsPersistConfig,
} from "state/tree/receivedAcmeIds";
import referralRegistrations from "state/tree/referralRegistrations";
import refreshSessionToken from "state/tree/refreshSessionToken";
import sessionDetails, {
  persistConfig as sessionDetailsPersistConfig,
} from "state/tree/sessionDetails";
import settings, {
  persistConfig as settingsPersistConfig,
} from "state/tree/settings";
import {
  mintroutePersistConfig,
  mintrouteShopReducer,
  promoShopReducer,
  persistConfig as shopPersistConfig,
  shopReducer,
  vipShopReducer,
} from "state/tree/shop";
import specialOffers, {
  persistConfig as specialOffersPersistConfig,
} from "state/tree/specialOffers";
import stories from "state/tree/stories";
import streamsCache from "state/tree/streamsCache";
import subscriptionPerStreamerList from "state/tree/subscriptionPerStreamerList";
import subscriptionsPerStreamer from "state/tree/subscriptionsPerStreamer";
import suspensionStatus, {
  persistConfig as suspensionStatusPersistConfig,
} from "state/tree/suspensionStatus";
import tabsConfig, {
  persistConfig as tabsConfigPersistConfig,
} from "state/tree/tabsConfig";
import taskManager from "state/tree/taskManager";
import taxes from "state/tree/taxes";
import tcnn from "state/tree/tcnn";
import toast from "state/tree/toast";
import topBroadcasters from "state/tree/topBroadcasters";
import tournamentsLanding from "state/tree/tournamentsLanding";
import transactionStatus, {
  persistConfig as transactionStatusPersistConfig,
} from "state/tree/transactionStatusAnalytics";
import upgradedStreams from "state/tree/upgradedStreams";
import user, { persistConfig as userPersistConfig } from "state/tree/user";
import userPosts from "state/tree/userPosts";
import userSearch from "state/tree/userSearch";
import validation from "state/tree/validation";
import viewerSession from "state/tree/viewerSession";
import vipConfigs, { vipConfigsPersistConfig } from "state/tree/vipConfigs";
import visitor, {
  persistConfig as visitorPersistConfig,
} from "state/tree/visitor";
import { linkToBroadcast } from "ui/navigation/links";

const makePersistConfig = (key, baseConfig = {}) => ({
  ...baseConfig,
  key: `${getEnvironmentName()}:${key}`,
  storage,
});

export const history = createBrowserHistory();

const combineAndReorderReducers = (hash) =>
  combineReducers(Object.fromEntries(Object.entries(hash).sort()));

const composeEnhancers =
  typeof window === "object" && window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__
    ? window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__({
        // Specify extension’s options like name, actionsBlacklist, actionsCreators, serialize...
      })
    : compose;

function makeDynamicBatch() {
  let store;
  let debounced;
  let lastDelay;

  const batch = (notify) => {
    if (!store) {
      return notify();
    }
    const state = store.getState();
    if (
      getIsBatchingReduxStoreEnabled(state) &&
      typeof window !== "undefined" &&
      window.location.pathname.startsWith(linkToBroadcast)
    ) {
      const delay = getTimeForReduxBatchingStore(state);
      if (!debounced || lastDelay !== delay) {
        debounced = debounce((cb) => cb(), delay);
        lastDelay = delay;
      }
      debounced(notify);
    } else {
      notify();
    }
  };

  batch.setStore = (s) => {
    store = s;
  };

  return batch;
}

export const createAppStore = (
  state = undefined,
  middleware = [
    batchDispatchMiddleware,
    thunk,
    routerMiddleware(history),
    analyticsListener,
    validationMiddleware.middleware,
    acmeObserver,
    accountIdChangeObserver,
    pendingActionsListener,
    openCashierAfterAutoLoginFailure,
    openWelcomeOnboardingOfferAfterRegistrationMiddleware.middleware,
    clearBannerShownEventAfterRegistrationMiddleware.middleware,
    clearVisitorProfileAfterLoginMiddleware.middleware,
    pullEventListenerMiddleware.middleware,
    recommendationsSetupMiddleware,
    financeServiceAcmeMiddleware,
    settingServiceAcmeMiddleware,
    recommendationsEventsMiddleware,
    unlockListenerMiddleware,
    addChatGiftToPlayMiddleware.middleware,
    sendGiftQueue.listenerMiddleware,
    buyEntranceOnBalanceUpdate,
    BIAccountIdMiddleware,
  ]
) => {
  const dynamicBatch = makeDynamicBatch();
  const store = createStore(
    combineAndReorderReducers({
      router: connectRouter(history),
      login: persistReducer(
        makePersistConfig("login", loginPersistConfig),
        loginReducer
      ),
      user: persistReducer(makePersistConfig("user", userPersistConfig), user),
      connectionManager: persistReducer(
        makePersistConfig("connectionManager", cmPersistConfig),
        connectionManager
      ),
      followingList: persistReducer(
        makePersistConfig("followingList", followingListPersistConfig),
        followingList
      ),
      settings: persistReducer(
        makePersistConfig("settings", settingsPersistConfig),
        settings
      ),
      blockedUsers,
      configuration: persistReducer(
        makePersistConfig("configuration", configurationPersistConfig),
        configuration
      ),
      referralRegistrations,
      deviceInfo: persistReducer(
        makePersistConfig("deviceInfo", deviceInfoPersistConfig),
        deviceInfo
      ),
      broadcast: persistReducer(
        makePersistConfig("broadcast", broadcastPersistConfig),
        broadcast
      ),
      landingPagePhoenix: persistReducer(
        makePersistConfig(
          "landingPagePhoenix",
          landingPagePhoenixConfigPersistConfig
        ),
        landingPagePhoenix
      ),
      personalOffers: persistReducer(
        makePersistConfig("personalOffers", personalOffersConfig),
        personalOffers
      ),
      giftsCache: persistReducer(
        makeIndexedDBPersistConfig("gifts", giftsCachePersistConfig),
        giftsCache
      ),
      giftDrawer: persistReducer(
        makeIndexedDBPersistConfig("giftDrawer", giftDrawerPersistConfig),
        giftDrawer
      ),
      customGift,
      customDrawer: persistReducer(
        makePersistConfig("customDrawer", customDrawerPersistConfig),
        customDrawer
      ),
      nearbyCountries: persistReducer(
        makePersistConfig("nearbyCountries", nearbyCountriesPersistConfig),
        nearbyCountries
      ),
      profilesCache: persistReducer(
        makePersistConfig("profilesCache", profilesCachePersistConfig),
        profilesCache
      ),
      vipConfigs: persistReducer(
        makePersistConfig("vipConfigs", vipConfigsPersistConfig),
        vipConfigs
      ),
      shop: persistReducer(
        makePersistConfig("shop", shopPersistConfig),
        shopReducer
      ),
      promoShop: promoShopReducer,
      vipShop: persistReducer(
        makePersistConfig("vipShop", shopPersistConfig),
        vipShopReducer
      ),
      mintrouteShop: persistReducer(
        makePersistConfig("mintrouteShop", mintroutePersistConfig),
        mintrouteShopReducer
      ),
      giftingRequests,
      modal,
      subscriptionsPerStreamer,
      afterPurchaseTaskManager,
      bottomScreen,
      subscriptionPerStreamerList,
      viewerSession,
      topBroadcasters,
      currencies,
      liveStreamsFeed: persistReducer(
        makePersistConfig("liveStreamsFeed", liveStreamsFeedPersistConfig),
        liveStreamsFeed
      ),
      notifications,
      postsFeed,
      networkAvailability,
      streamsCache,
      upgradedStreams,
      navigation,
      userSearch,
      userPosts,
      nftAuction,
      tcnn,
      bottomSheet,
      bannerConfigs,
      bannerGlobalConfigs,
      autoLogin,
      validation,
      taxes,
      profileSettings: profileSettingsSlice,
      liveNotifications: liveNotificationsSlice,
      guardian,
      tabsConfig: persistReducer(
        makePersistConfig("tabsConfig", tabsConfigPersistConfig),
        tabsConfig
      ),
      taskManager,
      specialOffers: persistReducer(
        makePersistConfig("specialOffers", specialOffersPersistConfig),
        specialOffers
      ),
      transactionStatus: persistReducer(
        makePersistConfig(
          TRANSACTION_STATUS_KEY,
          transactionStatusPersistConfig
        ),
        transactionStatus
      ),
      captcha: persistReducer(
        makePersistConfig("captcha", captchaPersistConfig),
        captcha
      ),
      intercom,
      leaderBoardLanding,
      tournamentsLanding,
      creditCards: persistReducer(
        makePersistConfig("creditCards", creditCardsPersistConfig),
        creditCards
      ),
      countries,
      countryByIp,
      chat,
      messageRequest,
      chatGiftAnimation,
      premiumMessage: persistReducer(
        makePersistConfig("premiumMessage"),
        premiumMessage
      ),
      welcomePremiumOffer,
      visitorProfile,
      visitorCashier,
      marketing: persistReducer(
        makePersistConfig("marketing", marketingPersistConfig),
        marketing
      ),
      toast,
      dropdownMenu,
      stories,
      suspensionStatus: persistReducer(
        makePersistConfig("suspensionStatus", suspensionStatusPersistConfig),
        suspensionStatus
      ),
      receivedAcmeIds: persistReducer(
        makePersistConfig("receivedAcmeIds", receivedAcmeIdsPersistConfig),
        receivedAcmeIds
      ),
      refreshSessionToken,
      sessionDetails: persistReducer(
        makePersistConfig("sessionDetails", sessionDetailsPersistConfig),
        sessionDetails
      ),
      visitor: persistReducer(
        makePersistConfig("visitor", visitorPersistConfig),
        visitor
      ),
      socialGamesLobby,
      socialGamesGame,
      socialGamesInStreamLobby,
      socialGamesFreeSpins,
      homePageV1: persistReducer(
        makePersistConfig("homePageV1", homePageV1PersistConfig),
        homePageV1
      ),
      homePageGames,
      homePageProfiles,
      phoneNumberSettings: persistReducer(
        makePersistConfig(
          "phoneNumberSettings",
          phoneNumberSettingsPersistConfig
        ),
        phoneNumberSettings
      ),
      welcomeOnboardingOffer,
      appeal,
      mos,
      mosChatV2,
      gifterUsersList,
      pointsUserInfo,
      redeemConfig,
      bonusWheel,
      homePageV2Nearby,
      homePageStreams,
      homePageCreators,
      pipsConfig,
      broadcastExternal: broadcastExternalReducer,
      tutorials: persistReducer(
        makePersistConfig("tutorials", tutorialsPersistConfig),
        tutorialsReducer
      ),
      referralProgram: persistReducer(
        makePersistConfig("referralProgram", referralProgramPersistConfig),
        referralProgram
      ),
      tangoAcademy: tangoAcademyReducer,
      feed,
      broadcastMasks: persistReducer(
        makePersistConfig("broadcastMasks", broadcastMasksPersistConfig),
        broadcastMasks
      ),
      invitations: invitationsReducer,
      loyaltyProgram,
      liveBar,
    }),
    state,
    composeEnhancers(
      applyMiddleware(...middleware),
      batchedSubscribe(dynamicBatch)
    )
  );
  dynamicBatch.setStore(store);

  return store;
};

const configureStore = () => {
  clearPersistLandingPageKey();
  const store = createAppStore();
  addAdditionalUnauthorizedErrorHandler(createUnauthorizedErrorHandler(store));
  addCaptchaHandler(createCaptchaHandler(store));
  addSessionTokenRefreshHandler(createSessionTokenRefreshHandler(store));
  injectJoshGoBack(store);
  const { afterStoreCreatedHook } = getHooks();
  afterStoreCreatedHook && afterStoreCreatedHook(store);

  return { store, persistor: persistStore(store) };
};

export default configureStore;
